const coppermind = (page) => `https://coppermind.net/wiki/${page}`;

const equipmentImage = (name) => `/images/equipment/${name}.webp?v=20260711-1`;

export const equipmentCatalog = {
  allomanticVial: {
    id: 'allomantic-vial',
    name: 'Allomantic metal vial',
    image: equipmentImage('allomantic-vial'),
    summary: 'Metal flakes suspended in a drinkable liquid provide a portable reserve.',
    details:
      'Allomancers normally ingest prepared metal flakes, feel the resulting reserve in their stomach, and burn that metal to access its power. Flaring produces a stronger effect while exhausting the reserve faster.',
    sourceUrl: coppermind('Allomancy#Mechanics'),
  },
  metalminds: {
    id: 'metalminds',
    name: 'Metalminds',
    image: equipmentImage('metalminds'),
    summary: 'Wearable metal stores an attribute that its Feruchemist can later tap.',
    details:
      'A Feruchemist fills a metalmind by temporarily reducing an attribute, then taps the stored attribute later. Drawing power faster than it was stored loses efficiency, so a short extreme burst costs more than an equivalent gentle use.',
    sourceUrl: coppermind('Feruchemy#Mechanics'),
  },
  shardblade: {
    id: 'living-shardblade',
    name: 'Living Shardblade',
    image: equipmentImage('shardblade'),
    summary: 'The Radiant\'s bonded spren can manifest instantly as a versatile Invested weapon.',
    details:
      'A living Shardblade can be summoned and dismissed at will, is unusually light for its size, and can change shape to suit its Radiant. It cuts nonliving material and severs the spiritual connection of living tissue rather than cutting it normally.',
    sourceUrl: coppermind('Shardblade#Radiant_Shardblades'),
  },
  shardplate: {
    id: 'living-shardplate',
    name: 'Living Shardplate',
    image: equipmentImage('shardplate'),
    summary: 'Lesser spren form responsive armor around a Radiant who has reached the required Ideal.',
    details:
      'Radiant Shardplate greatly increases protection, strength, speed, and agility. It can be summoned around the Radiant and, unlike ordinary armor, can resist highly Invested attacks. Most Orders acquire Plate around the Fourth Ideal.',
    sourceUrl: coppermind('Shardplate#Radiant_Shardplates'),
  },
};

const allomanticBudgets = {
  Iron: ['6 minutes of steady Pulling', '45 seconds if continuously flared'],
  Steel: ['5 minutes of steady Pushing', '40 seconds if continuously flared'],
  Tin: ['18 minutes of heightened senses', '2 minutes at an intense flare'],
  Pewter: ['8 minutes of enhanced strength and endurance', '60 seconds at an intense flare'],
  Zinc: ['12 minutes of controlled Rioting', '90 seconds influencing a large crowd'],
  Brass: ['12 minutes of controlled Soothing', '90 seconds influencing a large crowd'],
  Copper: ['20 minutes of coppercloud coverage', '2 minutes covering a broad area'],
  Bronze: ['20 minutes of Seeking', '2 minutes of focused, flared detection'],
  Aluminum: ['one complete purge of your metal reserves', 'single use'],
  Duralumin: ['one duralumin-enhanced burn', 'the burst consumes the active metal reserve'],
  Chromium: ['one decisive touch lasting up to 3 seconds', 'enough to drain one target'],
  Nicrosil: ['one decisive touch lasting up to 3 seconds', 'enough to flare one target\'s active power'],
  Gold: ['45 seconds of gold shadows', 'one sustained comparison with an alternate self'],
  Electrum: ['2 minutes of electrum shadows', '25 seconds if continuously flared'],
  Cadmium: ['4 minutes maintaining a time bubble', 'one bubble at maximum practical size'],
  Bendalloy: ['90 seconds total inside speed bubbles', 'enough for three short bubbles'],
  Atium: ['20 seconds of future shadows', '8 seconds if used at maximum intensity'],
};

const feruchemicalBudgets = {
  Iron: ['90 seconds at twice your normal mass', 'or 8 minutes at half your normal mass'],
  Steel: ['15 seconds at double physical speed', 'or one 4-second burst at roughly four times speed'],
  Tin: ['10 minutes with one sense doubled', 'choose sight, hearing, smell, taste, or touch'],
  Pewter: ['2 minutes at roughly one-and-a-half times normal strength', 'or a 20-second heavy-strength burst'],
  Zinc: ['3 minutes at double mental speed', 'or 30 seconds of extremely accelerated thought'],
  Brass: ['45 minutes of warmth in severe cold', 'or enough heat for one brief high-temperature contact'],
  Copper: ['six hours of indexed, perfectly preserved field memories', 'you must deliberately retrieve the relevant memory'],
  Bronze: ['8 hours without sleep or fatigue', 'or 40 minutes of intense wakefulness after exhaustion'],
  Aluminum: ['20 minutes with your Identity heavily suppressed', 'the effect is useful but difficult to exploit without preparation'],
  Duralumin: ['15 minutes of strengthened Connection', 'enough to communicate naturally with one unfamiliar community'],
  Chromium: ['one minute of strongly tapped Fortune', 'the result guides instinct rather than guaranteeing success'],
  Nicrosil: ['one modest charge of stored Investiture', 'enough to power one compatible Invested tool or ability briefly'],
  Gold: [
    ['can recover from one lethal injury', 'or close several serious wounds before returning to the fight'],
    ['can recover from two serious injuries', 'or mend one severe wound and clear the accompanying shock and exhaustion'],
    ['can recover from several moderate injuries', 'or keep functioning through a serious injury until you can reach safety'],
  ],
  Electrum: ['20 minutes of reinforced determination', 'or 2 minutes resisting overwhelming fear or despair'],
  Cadmium: ['12 minutes without breathing', 'or several shorter underwater or smoke-filled entries'],
  Bendalloy: ['two days of nutrition and one day of hydration', 'stored food and water can be tapped separately'],
  Atium: ['30 minutes appearing and functioning about ten years younger', 'greater youth would drain the reserve much faster'],
};

const reserveTiers = [
  { name: 'Light', multiplier: 0.7 },
  { name: 'Standard', multiplier: 1 },
  { name: 'Deep', multiplier: 1.35 },
];

const writtenNumbers = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  six: 6,
  eight: 8,
  twelve: 12,
  fifteen: 15,
  eighteen: 18,
  twenty: 20,
  thirty: 30,
  forty: 40,
  'forty-five': 45,
  ninety: 90,
};

function pick(items, rng) {
  return items[Math.floor(rng() * items.length)];
}

function scaleLeadingQuantity(amount, multiplier) {
  const match = amount.match(/^(\d+(?:\.\d+)?)\s+(seconds?|minutes?|hours?|days?)(.*)$/i);
  if (match) {
    const scaled = Math.max(1, Math.round(Number(match[1]) * multiplier));
    return `${scaled} ${match[2]}${match[3]}`;
  }

  const writtenMatch = amount.match(/^(one|two|three|four|six|eight|twelve|fifteen|eighteen|twenty|thirty|forty|forty-five|ninety)\s+(minutes?|hours?|days?)(.*)$/i);
  if (!writtenMatch) return amount;
  const scaled = Math.max(1, Math.round(writtenNumbers[writtenMatch[1].toLowerCase()] * multiplier));
  return `${scaled} ${writtenMatch[2]}${writtenMatch[3]}`;
}

function scenarioBudget(kind, metal, lines, rng) {
  const selectedLines = Array.isArray(lines[0]) ? pick(lines, rng) : lines;
  const tier = pick(reserveTiers, rng);
  return {
    kind,
    metal,
    label: `${metal} reserve`,
    amount: scaleLeadingQuantity(selectedLines[0], tier.multiplier),
    detail: `${tier.name} reserve. ${scaleLeadingQuantity(selectedLines[1], tier.multiplier)}`,
    isScenarioEstimate: true,
  };
}

export function buildAllomanticLoadout(
  power,
  { versatile = false, availablePowers = [power], rng = Math.random } = {}
) {
  const powers = versatile
    ? [power, ...availablePowers.filter((candidate) => candidate.id !== power.id)]
    : [power];
  return {
    equipment: [equipmentCatalog.allomanticVial],
    reserves: powers.map((candidate) =>
      scenarioBudget(
        'Allomancy',
        candidate.name,
        allomanticBudgets[candidate.name] || ['one decisive use', 'the exact duration depends on intensity'],
        rng
      )
    ),
    loadoutLine: versatile
      ? `You carry two mixed-metal vials. ${power.name} is foregrounded, but every metal in your full reserve inventory remains available.`
      : `You carry one prepared vial of ${power.name.toLowerCase()} flakes and have already swallowed a usable reserve.`,
  };
}

export function buildFeruchemicalLoadout(
  power,
  { versatile = false, availablePowers = [power], rng = Math.random } = {}
) {
  const powers = versatile
    ? [power, ...availablePowers.filter((candidate) => candidate.id !== power.id)]
    : [power];
  return {
    equipment: [equipmentCatalog.metalminds],
    reserves: powers.map((candidate) =>
      scenarioBudget(
        'Feruchemy',
        candidate.name,
        feruchemicalBudgets[candidate.name] || ['one decisive use', 'rapid tapping wastes part of the stored attribute'],
        rng
      )
    ),
    loadoutLine: versatile
      ? `You wear a charged set of metalminds. ${power.name} is foregrounded, but every metal in your full reserve inventory remains available.`
      : `You wear a charged ${power.name.toLowerCase()} metalmind that only you can tap.`,
  };
}

const stormlightBudgets = {
  2: [
    ['2 minutes holding Stormlight', 'No breath is needed while holding it. Spend the reserve on a non-lethal healing, or on one large Surgebinding effect covering about 10 square feet after holding it for 1 minute.'],
    ['3 minutes holding Stormlight', 'No breath is needed while holding it. Spend the reserve on two non-lethal healings, or on one large Surgebinding effect covering about 15 square feet after holding it for 1 minute.'],
  ],
  3: [
    ['4 minutes holding Stormlight', 'No breath is needed while holding it. Spend the reserve on two non-lethal healings, or on one large Surgebinding effect covering about 20 square feet.'],
    ['5 minutes holding Stormlight', 'No breath is needed while holding it. Spend the reserve on one serious healing, or on one large Surgebinding effect covering about 25 square feet.'],
  ],
  4: [
    ['6 minutes holding Stormlight', 'No breath is needed while holding it. Spend the reserve on one serious healing or one large Surgebinding effect covering about 30 square feet; using Shardplate drains this reserve faster.'],
    ['8 minutes holding Stormlight', 'No breath is needed while holding it. Spend the reserve on two serious healings or one large Surgebinding effect covering about 40 square feet; using Shardplate drains this reserve faster.'],
  ],
};

export function buildRadiantLoadout(order, ideal, { rng = Math.random } = {}) {
  const equipment = [];
  const stormlightBudget = pick(stormlightBudgets[ideal], rng);
  if (ideal >= 3) equipment.push(equipmentCatalog.shardblade);
  if (ideal >= 4) equipment.push(equipmentCatalog.shardplate);

  return {
    equipment,
    reserves: [
      {
        kind: 'Surgebinding',
        label: 'Stormlight reserve',
        amount: stormlightBudget[0],
        detail: stormlightBudget[1],
        isScenarioEstimate: true,
      },
    ],
    oathLevel: ideal,
    oathLabel: `${order.name.endsWith('s') ? order.name.slice(0, -1) : order.name} of the ${ordinal(ideal)} Ideal`,
    loadoutLine:
      ideal === 4
        ? 'Your bond grants both a living Shardblade and living Shardplate.'
        : ideal === 3
          ? 'Your bonded spren can manifest as a living Shardblade; you have not earned Shardplate.'
          : 'You can Surgebind, but your bonded spren cannot yet manifest as a Shardblade or Shardplate.',
  };
}

function ordinal(value) {
  if (value === 2) return 'Second';
  if (value === 3) return 'Third';
  if (value === 4) return 'Fourth';
  return `${value}th`;
}
